import { useEffect } from 'react';

import { Flex } from '@grupoboticario/flora-react';
import { InformationCircleIcon, ClockIcon } from '@grupoboticario/flora-react-icons';

import { ErrorBoundaries, ResponsiveFloraTypography } from 'components';
import { getFormattedPrice } from 'helpers';
import { useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './Rank.styles';
import { useLoyaltyIncentiveCampaign, useRanking } from '../../hooks';

import type { AxiosResponse } from 'axios';

export const Rank = () => {
  const { data: incentiveCampaign } = useLoyaltyIncentiveCampaign();
  const { data, lastUpdate, isError, error: rankingError, isSuccess, isLoading } = useRanking();
  const { sendDataToGTM } = useTracking();

  useEffect(() => {
    if ((isSuccess && data !== undefined) || (isError && data !== undefined)) {
      const error = rankingError as AxiosResponse;
      newRelicMonitoringService.addPageAction('callback', {
        event_name: 'ranking_campanha_de_incentivos',
        page_title: 'Campanha de Incentivos',
        cd_interaction_detail: isSuccess ? 'sucesso' : `erro: ${error.status}`,
      });

      sendDataToGTM({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'callback_ranking_campanha_de_incentivos',
          cd_detail: isSuccess ? 'sucesso' : `erro: ${error.status}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess]);

  if (isLoading) return null;

  if (isError) {
    return (
      <ErrorBoundaries.ReloadPage
        title='Tivemos um problema para exibir o ranking.'
        data-autotagging-flora-section={document}
      />
    );
  }

  return (
    <Flex direction='column' gap='$8'>
      <Flex direction='column' gap='$6'>
        <ResponsiveFloraTypography fontSize='title' fontWeight='bold' as='h2'>
          Como funciona
        </ResponsiveFloraTypography>

        <Flex direction='column' gap='$4'>
          <ResponsiveFloraTypography color='$nonInteractivePredominant'>
            {incentiveCampaign?.description}
          </ResponsiveFloraTypography>
        </Flex>

        <Styles.RankingWrapper size='medium'>
          <Flex direction='column' gap='$4'>
            <Styles.TotalCard>
              <Flex direction='column' gap='$4' align='center'>
                <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold'>
                  Acumulado no período
                </ResponsiveFloraTypography>

                <ResponsiveFloraTypography
                  fontSize='subtitle'
                  color='$nonInteractiveEmphasis'
                  fontWeight='bold'
                >
                  {getFormattedPrice(Number(data?.resellerPurchaseAmount))}
                </ResponsiveFloraTypography>
              </Flex>
            </Styles.TotalCard>

            <Styles.InfoCard>
              <Flex gap='$2'>
                <Styles.InfoIcon>
                  <InformationCircleIcon size={20} />
                </Styles.InfoIcon>

                <ResponsiveFloraTypography>
                  Para a premiação TOP {incentiveCampaign?.limit}, somamos as compras de produtos
                  elegíveis a partir do ciclo que você realizou a sua inscrição na campanha.
                </ResponsiveFloraTypography>
              </Flex>
            </Styles.InfoCard>
          </Flex>

          <Flex direction='column' gap='$4'>
            <Styles.TierCard>
              <ResponsiveFloraTypography fontWeight='bold'>
                TOP {incentiveCampaign?.limit} {data?.tierName && `- ${data.tierName}`}
              </ResponsiveFloraTypography>
            </Styles.TierCard>

            {data?.ranking?.length ? (
              <Styles.PositionsRankingWrapper>
                <Styles.PositionsRankingCard data-title>
                  <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                    Posição
                  </ResponsiveFloraTypography>
                  <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                    Valor
                  </ResponsiveFloraTypography>
                </Styles.PositionsRankingCard>

                {data?.position ? (
                  <Styles.RankPosition>
                    <ResponsiveFloraTypography fontSize='bodyLargeShort' color='$backgroundPrimary'>
                      Você está na posição {data.position}!
                    </ResponsiveFloraTypography>
                  </Styles.RankPosition>
                ) : null}

                <Styles.PositionsList>
                  {data.ranking.map((rank, index) => (
                    <Styles.PositionsRankingCard key={`positions-card-${index}`}>
                      <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                        {rank.position}
                      </ResponsiveFloraTypography>
                      <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                        {getFormattedPrice(rank.purchaseAmount)}
                      </ResponsiveFloraTypography>
                    </Styles.PositionsRankingCard>
                  ))}
                </Styles.PositionsList>
              </Styles.PositionsRankingWrapper>
            ) : null}

            <Styles.InfoCard>
              {lastUpdate ? (
                <Flex justify='space-between'>
                  <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                    Última atualização
                  </ResponsiveFloraTypography>
                  <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                    {lastUpdate}
                  </ResponsiveFloraTypography>
                </Flex>
              ) : (
                <Flex gap='$2'>
                  <Styles.InfoIcon>
                    <ClockIcon size={20} color='$nonInteractiveAuxiliar' />
                  </Styles.InfoIcon>

                  <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
                    O ranking é atualizado uma vez por dia! Continue cumprindo os desafios e
                    acompanhando as posições.
                  </ResponsiveFloraTypography>
                </Flex>
              )}
            </Styles.InfoCard>
          </Flex>
        </Styles.RankingWrapper>
      </Flex>
    </Flex>
  );
};
